import api, { ensureFreshSession } from './api';

export interface QuoteRequestItem {
  productId: string;
  quantity: number;
  billingMode: string;
  configuration: Record<string, string>;
}

export interface QuoteRequest {
  currency: 'EUR' | 'USD';
  items: QuoteRequestItem[];
  couponCode?: string;
  affiliateCode?: string;
  metadata?: Record<string, string>;
  useWalletBalance?: boolean;
}

export interface QuoteResponseItem {
  productId: string;
  quantity: number;
  currency: string;
  unitAmount: string;
  totalAmount: string;
  pricingSource: string;
  volumePercentOff: string | null;
  pricingCampaignId: string | null;
  billingType: string;
  configuration: Record<string, string | number>;
  unitAmountMinor: number;
  totalAmountMinor: number;
}

export interface QuoteResponse {
  quoteId: string;
  providerId: string;
  identityId: string | null;
  currency: string;
  totalAmount: string;
  oneTimeAmount: string;
  subscriptionAmount: string;
  discountAmount: string;
  totalAmountAfterDiscount: string;
  couponCode: string | null;
  affiliateCode: string | null;
  items: QuoteResponseItem[];
  totalAmountMinor: number;
  discountAmountMinor: number;
  totalAmountAfterDiscountMinor: number;
  oneTimeAmountMinor: number;
  subscriptionAmountMinor: number;
  walletAvailableAmount?: string;
  walletAppliedAmount?: string;
  totalAmountDueAfterWallet?: string;
  walletCanFullyCover?: boolean;
  isBlacklisted?: boolean;
  blacklistReason?: string | null;
}

export interface CheckoutAddress {
  address_type: 'invoice';
  street: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

export interface CheckoutStartRequest {
  paymentProvider: 'stripe' | 'paypal' | 'bitpay' | 'wallet';
  quoteId: string;
  customerEmail: string;
  customerFirstname?: string;
  customerLastname?: string;
  isCompany: boolean;
  company: string | null;
  addresses: CheckoutAddress[];
  languageCode: string;
  timezone: string;
  receiveMarketingMails: boolean;
  userNotes: string;
  checkoutReferenceId: string;
  useWalletBalance?: boolean;
}

export interface CheckoutStartResponse {
  order_id: string;
  identity_id: string;
  billing_contract_id: string;
  currency: string;
  total_amount: string;
  one_time_amount_minor: number;
  subscription_amount_minor: number;
  payment_intent_id: string;
  stripe_session_id?: string;
  checkout_url: string;
  isBlacklisted?: boolean;
  blacklistReason?: string | null;
}

export const fetchQuote = async (request: QuoteRequest): Promise<QuoteResponse> => {
  await ensureFreshSession();
  const response = await api.post('/checkout/quote', request);
  return response.data;
};

export const startCheckout = async (request: CheckoutStartRequest): Promise<CheckoutStartResponse> => {
  await ensureFreshSession();
  const response = await api.post('/checkout/start', request);
  return response.data.data ?? response.data;
};

export interface VerifyPaymentRawResponse {
  providerId: string;
  orderId: string;
  status: string;
  isFinal: boolean;
  pollAfterMs: number;
  orderStatus: string;
  paymentStatus: string;
  paymentProvider: string;
  providerSessionId?: string;
  providerPaymentId?: string;
  updatedAt: string;
}

export interface VerifyPaymentResult {
  raw: VerifyPaymentRawResponse;
  resolved: 'pending' | 'completed' | 'failed' | 'expired';
  pollAfterMs: number;
}

const resolveStatus = (raw: VerifyPaymentRawResponse): VerifyPaymentResult['resolved'] => {
  if (raw.status === 'paid' || raw.paymentStatus === 'Succeeded') return 'completed';
  if (raw.status === 'failed' || raw.paymentStatus === 'Failed') return 'failed';
  if (raw.status === 'expired') return 'expired';
  return 'pending';
};

export const verifyPayment = async (token: string): Promise<VerifyPaymentResult> => {
  const response = await api.get(`/checkout/verify-payment/${token}`);
  const raw: VerifyPaymentRawResponse = response.data.data ?? response.data;
  return {
    raw,
    resolved: resolveStatus(raw),
    pollAfterMs: raw.isFinal ? 0 : (raw.pollAfterMs || 2000),
  };
};
